import ArrowPrev from '/public/icons/icon-screenshots-prev.svg'
import ArrowNext from '/public/icons/icon-screenshots-next.svg'
import SlickSlider from '../../../../../../../../../elements/SlickSlider'
import styles from './styles.module.scss'
import LazyImage from '../../../../../../../../../elements/LazyImage'
import { withComma } from '../../../../../../../../../helpers/withComma'
import { Formik, Form, Field } from 'formik'
import Button from '../../../../../../../../../elements/Button'
import { withModifiers } from '../../../../../../../../../helpers/withModifiers'
import axios from '/components/configs/axios'
import { useState, useMemo } from 'react'
import { useRouter } from 'next/router'
import moment from 'moment'

const formatDate = (date) => {
  if (!date) return ''
  const d = moment(date).utc()
  return d.format('MMM DD, YYYY')
}

const formatDateForAPI = (date) => {
  if (!date) return ''
  const d = moment(date).utc()
  return d.format('YYYY-MM-DD')
}

const Details = ({
  property,
  startDate,
  endDate,
  bedrooms,
  guests,
  setUpdatedPrice,
  updatedPrice,
  setCoupon,
  totalWithoutStripe,
  taxesAndFees,
  totalCostPerNights,
  bonuses,
  numberOfFreeNights,
  freeNightsCost,
  freeNightsOldPrice,
  translations
}) => {
  const [error, setError] = useState(false)
  const router = useRouter()

  // Memoize active bonuses to avoid filtering twice
  const activeBonuses = useMemo(() => {
    if (!bonuses) return []
    return Object.keys(bonuses).filter((bonus) => bonuses[bonus])
  }, [bonuses])

  return (
    <div className={styles.details}>
      {property.pictures && (
        <SlickSlider
          nextArrow={<ArrowNext />}
          prevArrow={<ArrowPrev />}
          slidesToShow={1}
          className={styles.details__slider}
        >
          {property.pictures &&
            property.pictures
              .slice(0, 7)
              .map((item, index) => (
                <LazyImage key={index} src={item.original} index />
              ))}
        </SlickSlider>
      )}
      <h3 className={styles.details__title}>{property?.propertyTitle}</h3>
      <div className={withModifiers('details__row', ['merged'], styles)}>
        <span>{translations.popup.arrival[router.locale]}</span>

        <span>{formatDate(startDate)}</span>
      </div>
      <div className={withModifiers('details__row', ['merged'], styles)}>
        <span>{translations.popup.departure[router.locale]}</span>

        <span>{formatDate(endDate)}</span>
      </div>
      <div className={withModifiers('details__row', ['merged'], styles)}>
        <span>{translations.popup.bedroom[router.locale]}</span>

        <span className={styles.details__bedroom}>{bedrooms}</span>
      </div>
      {guests && (
        <div className={withModifiers('details__row', ['merged'], styles)}>
          <span>{translations.popup?.guests[router.locale]}</span>

          <span>{guests}</span>
        </div>
      )}
      {activeBonuses.length > 0 && (
        <div className={styles.details__row}>
          <span>{translations.popup.bonuses[router.locale]}</span>
          <div>
            {activeBonuses.map((bonus, index, array) => (
              <span key={bonus}>
                {bonus === 'night' &&
                  numberOfFreeNights &&
                  `${numberOfFreeNights} ${
                    translations.popup.free[router.locale]
                  } `}
                {bonus !== 'night' &&
                  bonus.charAt(0).toUpperCase() + bonus.slice(1)}
                {bonus === 'night' &&
                  (Number(numberOfFreeNights) > 1
                    ? translations.popup.nights[router.locale]
                    : translations.popup.night[router.locale])}
                {index !== array.length - 1 ? ', ' : ''}
              </span>
            ))}
          </div>
        </div>
      )}
      {freeNightsOldPrice && (
        <div className={styles.details__row}>
          <span>{translations.popup.subtotal[router.locale]}</span>

          <span>${withComma(freeNightsOldPrice)}</span>
        </div>
      )}

      {freeNightsCost !== 0 && (
        <div className={withModifiers('details__row', ['merged'], styles)}>
          <span>{translations.popup.freeNight[router.locale]}</span>

          <span>- ${withComma(freeNightsCost)}</span>
        </div>
      )}

      <div className={styles.details__row}>
        <span>{translations.popup.total[router.locale]}</span>

        <span>${withComma(totalCostPerNights)}</span>
      </div>

      {updatedPrice && (
        <div className={withModifiers('details__row', ['merged'], styles)}>
          <span>{translations.popup.coupon[router.locale]}</span>

          <span>- ${withComma(updatedPrice.discount)}</span>
        </div>
      )}

      <div className={withModifiers('details__row', ['merged'], styles)}>
        <span>{translations.popup.taxesFees[router.locale]}</span>

        <span>
          + $
          {updatedPrice
            ? withComma(updatedPrice.tax10)
            : withComma(taxesAndFees)}
        </span>
      </div>
      <div className={styles.details__row}>
        <span>{translations.popup.finalPrice[router.locale]}</span>

        <span>
          $
          {updatedPrice
            ? withComma(updatedPrice.totalWith10Taxes)
            : withComma(totalWithoutStripe)}
        </span>
      </div>
      <div
        dangerouslySetInnerHTML={{
          __html: translations.popup.contact[router.locale]
        }}
        className={styles.details__note}
      ></div>
      <div className={styles.details__deposit_amount}>
        25% deposit: $
        {updatedPrice
          ? withComma(Math.round(updatedPrice.totalWith10Taxes * 0.25))
          : withComma(Math.round(totalWithoutStripe * 0.25))}
      </div>
      <Formik enableReinitialize initialValues={{ coupon: '' }}>
        {({ values, setFieldValue, handleBlur, isSubmitting }) => (
          <Form className={styles.form}>
            <Field
              variant={['full-width']}
              maxLength={30}
              placeholder={translations.popup.coupon[router.locale]}
              name="coupon"
              onChange={(e) => {
                setFieldValue(
                  'coupon',
                  e.target.value.replace(/[^a-zA-Z0-9]/g, '')
                )
              }}
              onBlur={handleBlur}
              className={styles.form__field}
            />

            <Button
              variant={['secondary']}
              className={styles.form__button}
              disabled={isSubmitting}
              arrow={false}
              aria-label={translations.popup.applyCoupon[router.locale]}
              name="apply-coupon-button"
              onClick={() =>
                axios
                  .post('reservation/price-calculation', {
                    lang: router.locale,
                    id: property?.resource_id,
                    startDate: formatDateForAPI(startDate),
                    endDate: formatDateForAPI(endDate),
                    bedrooms: bedrooms,
                    discountCode: values.coupon
                  })
                  .then((res) => {
                    const data = res.data

                    if (data.discount) {
                      setUpdatedPrice(data)
                      setCoupon(values.coupon)
                      setError(false)
                    } else {
                      setError(true)
                    }
                  })
                  .catch((error) => {
                    if (error.response) {
                      setError(error.response.status)
                    } else {
                      setError('network')
                    }
                  })
              }
            >
              {translations.popup.applyCoupon[router.locale]}
            </Button>
          </Form>
        )}
      </Formik>
      <div className={styles.form__error}>
        {error === 404 && translations.popup.invalidCoupon[router.locale]}
        {error === 'network' && translations.popup.networkError[router.locale]}
        {error &&
          error !== 404 &&
          error !== 'network' &&
          error !== false &&
          translations.popup.generalError[router.locale]}
      </div>
    </div>
  )
}

export default Details
